/**
 * Which concepts a coach has read.
 *
 * One user_data key holding a map of concept key to ISO timestamp. Not
 * lab_seen, which is keyed by item type and id against content rows: a
 * concept is an authored module in the bundle rather than a row, so it has
 * no id to mark. Keeping it here is also why the Concepts lane shipped
 * without a migration.
 *
 * Split from index.js so the registry itself imports no Supabase client.
 */
import { loadUserData, saveUserData } from "../utils/userData";
import { supabase } from "../../supabaseClient";

const READS_KEY = "concept_reads";

export async function loadConceptReads() {
  try {
    const v = await loadUserData(READS_KEY, {});
    return v && typeof v === "object" && !Array.isArray(v) ? v : {};
  } catch { return {}; }
}

export async function markConceptRead(key) {
  const reads = await loadConceptReads();
  if (reads[key]) return reads;
  const next = { ...reads, [key]: new Date().toISOString() };
  // A failed write is not worth interrupting a read for: the lane already
  // shows it as read for this session, and the next open re-marks it.
  try { await saveUserData(READS_KEY, next); } catch {}
  return next;
}

/**
 * Concepts released by a Wednesday Drop: key -> that drop's publish time.
 * Only published drops whose time has come count (the join filters on the
 * drop, so an admin's drafts don't release anything for them either).
 * Empty on any error, which keeps held concepts held.
 */
export async function loadReleasedConcepts() {
  try {
    const { data, error } = await supabase.from("drop_items")
      .select("item_ref, drops!inner(status, publish_at)")
      .eq("item_type", "concept")
      .eq("drops.status", "published")
      .lte("drops.publish_at", new Date().toISOString());
    if (error) return {};
    const out = {};
    for (const row of data || []) {
      const at = row.drops?.publish_at;
      if (at && (!out[row.item_ref] || at < out[row.item_ref])) out[row.item_ref] = at;
    }
    return out;
  } catch { return {}; }
}
